// In-app phase grouping for the operational dashboard.
// Note: this is DIFFERENT from Stripe phase-pair SKU structure (PHASE_PAIRS in /lib/sku.js).
// Stripe phases: P1=A+B, P2=C+D, P3=E+F, P4=G+H (curriculum bundles).
// Dashboard phases: Foundation=A/B/C, Builder=D/E/F, Executor=G/H (operational workflow).
//
// Per Team Lead directive.

export const DASHBOARD_PHASES = [
    {
        id: "foundation",
        name: "Foundation",
        letters: ["A", "B", "C"],
        description: "Set the architecture, define the system, write the doctrine.",
        accent: "cyan",
    },
    {
        id: "builder",
        name: "Builder",
        letters: ["D", "E", "F"],
        description: "Design, engineer, finalize. Building the actual machine.",
        accent: "cyan-strong",
    },
    {
        id: "executor",
        name: "Executor",
        letters: ["G", "H"],
        description: "Launch, scale, hand off. Operational deployment at speed.",
        accent: "gold",
    },
];

export function phaseForLetter(letter) {
    if (!letter) return null;
    const L = letter.toUpperCase();
    return DASHBOARD_PHASES.find((p) => p.letters.includes(L)) || null;
}

/**
 * Compute the user's "active" dashboard phase.
 * Heuristic: pick the phase containing the user's last_active_module
 * if any; else pick the earliest phase that has at least one unlocked
 * letter but isn't fully completed.
 */
export function getActivePhase(unlockedLetters = [], lastActive = null, completedModules = []) {
    if (lastActive) {
        const p = phaseForLetter(lastActive);
        if (p) return p;
    }
    const owned = new Set((unlockedLetters || []).map((l) => l.toUpperCase()));
    const done = new Set((completedModules || []).map((l) => l.toUpperCase()));
    for (const p of DASHBOARD_PHASES) {
        if (p.letters.some((L) => owned.has(L) && !done.has(L))) return p;
    }
    // Default: first phase the user owns any letter in
    for (const p of DASHBOARD_PHASES) {
        if (p.letters.some((L) => owned.has(L))) return p;
    }
    return DASHBOARD_PHASES[0];
}

export function phaseCompletionPct(phase, completedModules = []) {
    if (!phase) return 0;
    const done = new Set((completedModules || []).map((l) => l.toUpperCase()));
    const total = phase.letters.length;
    const completed = phase.letters.filter((L) => done.has(L)).length;
    return Math.round((completed / total) * 100);
}

export function phaseStatus(phase, unlockedLetters = [], completedModules = []) {
    const pct = phaseCompletionPct(phase, completedModules);
    const owned = new Set((unlockedLetters || []).map((l) => l.toUpperCase()));
    if (pct === 100) return "complete";
    if (phase.letters.some((L) => owned.has(L))) return "active";
    return "dim"; // not unlocked yet — but NEVER use the word "locked" per directive
}
